import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { BLUE, BORDER, TEXT, TEXT2, TEXT3, SUCCESS } from "../../shared";

// ─────────────────────────────────────────────────────────────────────────
// Operation Showroom — the Website-tab preview frame.
//
// TRUE-PROPORTION RENDER: the iframe is laid out at the device's *native* CSS
// size (e.g. 1280×800) and scaled with transform:scale(S) where
// S = containerWidth / deviceWidth. The scaled wrapper's height is
// deviceHeight × S, so the rendered content width always equals the container
// width — no letterbox, no dead bars.
//
// ONE FRAME: a single glass wrapper (radius 28, padding 20) clips the inner
// stack (chrome bar + viewport) to radius 8 (28 − 20). No double border, no
// 2px seam.
//
// The iframe is non-interactive (pointer-events:none); hovering reveals a
// scrim with "Open full preview" / "Edit this page".
//
// Full spec: src/docs/showroom-spec.md
// ─────────────────────────────────────────────────────────────────────────

export const SHOWROOM_BRAND = "#1A47F0";

const DEVICES = {
  desktop: { id: "desktop", label: "Desktop", w: 1280, h: 800 },
  tablet:  { id: "tablet",  label: "Tablet",  w: 834,  h: 1112 },
  phone:   { id: "phone",   label: "Phone",   w: 390,  h: 844 },
};

const DEVICE_PREF_KEY = "vella.showroom.device";
const FRAME_RADIUS = 28;
const FRAME_PAD = 20;
const INNER_RADIUS = FRAME_RADIUS - FRAME_PAD; // 8
const CHROME_H = 40;
const LOAD_TIMEOUT_MS = 9000; // no load event by here → assume blocked / error

const GLASS = {
  background: "rgba(255,255,255,0.72)",
  backdropFilter: "blur(40px) saturate(180%)",
  WebkitBackdropFilter: "blur(40px) saturate(180%)",
};

// One-shot keyframes for the loading shimmer + scrim/fade transitions.
const SHOWROOM_CSS = `
@keyframes sr-shimmer { 0% { background-position: -160% 0 } 100% { background-position: 160% 0 } }
@keyframes sr-fade-in { from { opacity: 0 } to { opacity: 1 } }
.sr-frame .sr-scrim { opacity: 0; transition: opacity 200ms cubic-bezier(0.22,1,0.36,1); }
.sr-frame:hover .sr-scrim, .sr-frame:focus-within .sr-scrim { opacity: 1; }
`;

function readDevicePref() {
  try {
    const v = localStorage.getItem(DEVICE_PREF_KEY);
    if (v && DEVICES[v]) return v;
  } catch (_) {}
  return "desktop";
}

// ── DeviceToggle ─────────────────────────────────────────────────────────
function DeviceToggle({ value, onChange }) {
  return (
    <div
      role="radiogroup"
      aria-label="Preview device"
      style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 10, background: "rgba(255,255,255,0.55)", border: `1px solid ${BORDER}` }}
    >
      {Object.values(DEVICES).map((d) => {
        const active = value === d.id;
        return (
          <button
            key={d.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(d.id)}
            title={`${d.label} — ${d.w}×${d.h}`}
            style={{
              height: 24, padding: "0 10px", cursor: "pointer", borderRadius: 7,
              fontSize: 11, fontWeight: 700, letterSpacing: "0.01em",
              border: "none", color: active ? "#fff" : TEXT2,
              background: active ? BLUE : "transparent",
              transition: "background 160ms cubic-bezier(0.22,1,0.36,1), color 160ms",
            }}
          >
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

// ── BrowserChromeBar ─────────────────────────────────────────────────────
function BrowserChromeBar({ domain, device, onDevice, onOpen }) {
  const shortDomain = (domain || "preview.vella.app").replace(/^https?:\/\//i, "");
  return (
    <div
      style={{
        flex: "0 0 auto", height: CHROME_H, display: "flex", alignItems: "center", gap: 10,
        padding: "0 12px", borderBottom: `1px solid ${BORDER}`,
        background: "rgba(255,255,255,0.5)",
      }}
    >
      {/* 3 traffic-light dots — slate, not colored */}
      <div style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: TEXT3, opacity: 0.35 }} />
        ))}
      </div>

      {/* Centered domain pill */}
      <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
        <div
          style={{
            display: "inline-flex", alignItems: "center", gap: 7, maxWidth: "100%",
            height: 26, padding: "0 12px", borderRadius: 999,
            background: "rgba(255,255,255,0.7)", border: `1px solid ${BORDER}`,
            fontSize: 12, fontWeight: 600, color: TEXT2, minWidth: 0,
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto", opacity: 0.7 }}>
            <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
          </svg>
          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{shortDomain}</span>
          <span title="Live" style={{ flex: "0 0 auto", width: 7, height: 7, borderRadius: "50%", background: SUCCESS, boxShadow: `0 0 0 2px ${SUCCESS}22` }} />
        </div>
      </div>

      <DeviceToggle value={device} onChange={onDevice} />

      <button
        type="button"
        onClick={onOpen}
        title="Open in a new tab"
        style={{
          flex: "0 0 auto", height: 26, padding: "0 10px", cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 8,
          fontSize: 11, fontWeight: 700, color: TEXT2,
          background: "rgba(255,255,255,0.7)", border: `1px solid ${BORDER}`,
        }}
      >
        Open
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M8 7h9v9" /></svg>
      </button>
    </div>
  );
}

// ── ActionButton (unified hierarchy: primary / glass / ghost) ────────────
export function ActionButton({ variant = "glass", onClick, children, disabled }) {
  const base = {
    height: 40, padding: "0 20px", cursor: disabled ? "default" : "pointer",
    fontFamily: "inherit", fontSize: 13.5, fontWeight: 600, borderRadius: 999,
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
    transition: "transform 140ms cubic-bezier(.34,1.3,.5,1), filter 160ms, background 160ms",
    opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  };
  const skin =
    variant === "primary"
      ? { color: "#fff", background: SHOWROOM_BRAND, border: `1px solid ${SHOWROOM_BRAND}`, boxShadow: "0 10px 24px -10px rgba(26,71,240,0.55)" }
      : variant === "ghost"
      ? { color: TEXT2, background: "transparent", border: "1px solid transparent" }
      : { color: TEXT, background: "rgba(255,255,255,0.7)", border: `1px solid ${BORDER}`, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.85)" };
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="liquid-press"
      style={{ ...base, ...skin }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
    >
      {children}
    </button>
  );
}

// ── ShowroomFrame ────────────────────────────────────────────────────────
export default function ShowroomFrame({
  html,
  url,
  domain,
  title = "Your website",
  onEdit,          // "Edit website" (filled) + scrim "Edit this page"
  onVisit,         // "Visit site" (glass)
  onRedeploy,      // "Redeploy" (glass)
  onDomainSettings,// "Domain settings" (ghost)
  redeploying = false,
}) {
  const [device, setDevice] = useState(readDevicePref);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [containerW, setContainerW] = useState(0);

  const viewportRef = useRef(null);
  const loadTimerRef = useRef(null);

  const dev = DEVICES[device];
  const hasContent = !!(html || url);

  // Persist device choice.
  useEffect(() => {
    try { localStorage.setItem(DEVICE_PREF_KEY, device); } catch (_) {}
  }, [device]);

  // Measure the viewport width for the scale math (ResizeObserver keeps it
  // exact through window resizes and sidebar collapse).
  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    const measure = () => setContainerW(el.clientWidth);
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    } else {
      window.addEventListener("resize", measure);
    }
    return () => { if (ro) ro.disconnect(); else window.removeEventListener("resize", measure); };
  }, []);

  // Reset the loading veil whenever the source, device, or retry changes.
  // Arm a timeout so a blocked cross-origin site (X-Frame-Options) surfaces a
  // Retry affordance instead of an eternal shimmer.
  useEffect(() => {
    if (!hasContent) return undefined;
    setLoaded(false);
    setError(false);
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    loadTimerRef.current = setTimeout(() => setError(true), LOAD_TIMEOUT_MS);
    return () => { if (loadTimerRef.current) clearTimeout(loadTimerRef.current); };
  }, [html, url, device, retryKey, hasContent]);

  const handleLoad = useCallback(() => {
    if (loadTimerRef.current) { clearTimeout(loadTimerRef.current); loadTimerRef.current = null; }
    setError(false);
    setLoaded(true);
  }, []);

  const retry = useCallback(() => { setRetryKey((k) => k + 1); }, []);

  const openNewTab = useCallback(() => {
    if (url) { window.open(url, "_blank", "noopener"); return; }
    if (html) {
      const blobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      window.open(blobUrl, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    }
  }, [url, html]);

  const scale = containerW > 0 ? containerW / dev.w : 0;
  const scaledH = Math.round(dev.h * scale);
  const iframeProps = url && !html ? { src: url } : { srcDoc: html || "" };

  return (
    <div>
      <style>{SHOWROOM_CSS}</style>

      {/* ONE glass frame — radius 28, padding 20, inner clip 8 */}
      <div
        className="sr-frame"
        style={{
          position: "relative", borderRadius: FRAME_RADIUS, padding: FRAME_PAD,
          border: `1px solid ${BORDER}`, ...GLASS,
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.85), 0 24px 56px -22px rgba(39,92,115,0.28)",
        }}
      >
        <div style={{ borderRadius: INNER_RADIUS, overflow: "hidden", border: `1px solid ${BORDER}`, background: "#fff", display: "flex", flexDirection: "column" }}>
          <BrowserChromeBar domain={domain || url} device={device} onDevice={setDevice} onOpen={openNewTab} />

          {/* Viewport — height is driven by the true-proportion scale so there
              is never a letterbox band above or below the scaled page. */}
          <div
            ref={viewportRef}
            style={{
              position: "relative", width: "100%",
              height: hasContent && scaledH ? scaledH : 460,
              maxHeight: "70vh", overflow: "hidden", background: "#fff",
            }}
          >
            {hasContent ? (
              <>
                {/* Scaled render surface */}
                {scale > 0 && (
                  <div
                    style={{
                      width: dev.w, height: dev.h, transform: `scale(${scale})`,
                      transformOrigin: "top left",
                      transition: "transform 240ms cubic-bezier(0.22,1,0.36,1)",
                    }}
                  >
                    <iframe
                      key={`${device}-${retryKey}`}
                      title={title}
                      {...iframeProps}
                      onLoad={handleLoad}
                      onError={() => setError(true)}
                      loading="lazy"
                      sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                      style={{ width: dev.w, height: dev.h, border: "none", display: "block", background: "#fff", pointerEvents: "none" }}
                    />
                  </div>
                )}

                {/* Hover scrim — the only interactive layer over the frame */}
                <div
                  className="sr-scrim"
                  style={{
                    position: "absolute", inset: 0, zIndex: 3,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
                    background: "linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.34) 100%)",
                  }}
                >
                  <ActionButton variant="glass" onClick={openNewTab}>Open full preview</ActionButton>
                  <ActionButton variant="primary" onClick={onEdit}>Edit this page</ActionButton>
                </div>

                {/* Loading skeleton — shimmer until load, then 300ms fade */}
                {!loaded && !error && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute", inset: 0, zIndex: 4, background: "#fff",
                      opacity: loaded ? 0 : 1, transition: "opacity 300ms ease",
                    }}
                  >
                    <div style={{
                      position: "absolute", inset: 0,
                      background: "linear-gradient(100deg, #f2f5f7 30%, #e6ecf0 50%, #f2f5f7 70%)",
                      backgroundSize: "200% 100%", animation: "sr-shimmer 1.4s linear infinite",
                    }} />
                  </div>
                )}

                {/* Error / blocked — soft empty + Retry */}
                {error && (
                  <div
                    role="alert"
                    style={{
                      position: "absolute", inset: 0, zIndex: 5, background: "#fff",
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, textAlign: "center", padding: 24,
                    }}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(200,146,67,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#c89243" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>Couldn't load the preview</div>
                      <div style={{ fontSize: 12.5, color: TEXT3, marginTop: 4, maxWidth: 320 }}>The site may block embedding, or the connection is slow. Open it in a new tab, or try again.</div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <ActionButton variant="glass" onClick={retry}>Retry</ActionButton>
                      <ActionButton variant="primary" onClick={openNewTab}>Open in new tab</ActionButton>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ height: 460, display: "flex", alignItems: "center", justifyContent: "center", color: TEXT3, fontSize: 13 }}>
                Preview unavailable
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ACTION ROW — one primary, glass secondaries, ghost tertiary */}
      <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <ActionButton variant="primary" onClick={onEdit}>Edit website</ActionButton>
        {onVisit && <ActionButton variant="glass" onClick={onVisit}>Visit site ↗</ActionButton>}
        {onRedeploy && <ActionButton variant="glass" onClick={onRedeploy} disabled={redeploying}>{redeploying ? "Redeploying…" : "Redeploy"}</ActionButton>}
        {onDomainSettings && <ActionButton variant="ghost" onClick={onDomainSettings}>Domain settings</ActionButton>}
      </div>
    </div>
  );
}

// ── EmptyState — "You don't have a website yet" ──────────────────────────
export function ShowroomEmptyState({ onDesign }) {
  return (
    <div
      style={{
        marginTop: 14, textAlign: "center", padding: "48px 24px",
        borderRadius: FRAME_RADIUS, border: `1px solid ${BORDER}`, ...GLASS,
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.85), 0 24px 56px -22px rgba(39,92,115,0.22)",
      }}
    >
      <div style={{ width: 56, height: 56, borderRadius: 16, background: "rgba(26,71,240,0.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={SHOWROOM_BRAND} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
      </div>
      <h3 style={{ fontSize: 19, fontWeight: 700, color: TEXT, marginBottom: 6, letterSpacing: "-0.022em" }}>You don't have a website yet</h3>
      <p style={{ fontSize: 14, color: TEXT2, maxWidth: 440, margin: "0 auto 20px", lineHeight: 1.5 }}>
        Tell Vella about your business and she'll design, build, and deploy an award-winning custom website for you.
      </p>
      <ActionButton variant="primary" onClick={onDesign}>Design my website</ActionButton>
    </div>
  );
}
